import React, { useState } from 'react';
import { Box, Button, Typography, Modal, TextField, Grid, Card, CardContent, CardActions, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Header from '../components/Header';

const PropertyIssuesPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    const [propertyIssuesData, setPropertyIssuesData] = useState([
        {
            id: 1,
            description: 'Leaking roof in the kitchen',
            status: 'Open',
            propertyName: 'Summit Apartments',
            reportedBy: 'John Davis',
            reportDate: '2023-06-15',
            priority: 'High'
        },
        {
            id: 2,
            description: 'Faulty plumbing in the bathroom',
            status: 'In Progress',
            propertyName: 'Lakeside Condos',
            reportedBy: 'Sarah Williams',
            reportDate: '2023-06-10',
            priority: 'Medium'
        },
        {
            id: 3,
            description: 'Broken window in the living room',
            status: 'Resolved',
            propertyName: 'Oakview Heights',
            reportedBy: 'Michael Johnson',
            reportDate: '2023-06-05',
            priority: 'Low'
        },
        {
            id: 4,
            description: 'HVAC system not functioning properly',
            status: 'Open',
            propertyName: 'Parkside Residences',
            reportedBy: 'Emily Wilson',
            reportDate: '2023-06-18',
            priority: 'Urgent'
        }
    ]);

    const [selectedIssue, setSelectedIssue] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newIssue, setNewIssue] = useState({
        description: '',
        status: 'Open',
        propertyName: '',
        reportedBy: '',
        reportDate: new Date().toISOString().split('T')[0],
        priority: 'Medium'
    });

    // Handle delete action
    const handleDelete = (id) => {
        setPropertyIssuesData(propertyIssuesData.filter(item => item.id !== id));
    };

    // Open edit modal
    const openEditModal = (issue) => {
        setSelectedIssue(issue);
        setIsModalOpen(true);
    };

    // Close edit modal
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedIssue(null);
    };

    // Open add modal
    const openAddModal = () => {
        setIsAddModalOpen(true);
    };

    // Close add modal
    const closeAddModal = () => {
        setIsAddModalOpen(false);
        setNewIssue({
            description: '',
            status: 'Open',
            propertyName: '',
            reportedBy: '',
            reportDate: new Date().toISOString().split('T')[0],
            priority: 'Medium'
        });
    };

    // Handle edit form submission
    const handleEditSubmit = (event) => {
        event.preventDefault();
        const updatedIssue = {
            ...selectedIssue,
            description: event.target.description.value,
            status: event.target.status.value,
            propertyName: event.target.propertyName.value,
            reportedBy: event.target.reportedBy.value,
            reportDate: event.target.reportDate.value,
            priority: event.target.priority.value
        };

        setPropertyIssuesData(propertyIssuesData.map(item =>
            item.id === updatedIssue.id ? updatedIssue : item
        ));
        closeModal();
    };

    // Handle add form submission
    const handleAddSubmit = (event) => {
        event.preventDefault();
        const newId = Math.max(...propertyIssuesData.map(item => item.id)) + 1;
        const createdIssue = {
            id: newId,
            description: event.target.description.value,
            status: event.target.status.value,
            propertyName: event.target.propertyName.value,
            reportedBy: event.target.reportedBy.value,
            reportDate: event.target.reportDate.value,
            priority: event.target.priority.value
        };

        setPropertyIssuesData([...propertyIssuesData, createdIssue]);
        closeAddModal();
    };

    // Handle input change for new issue
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewIssue({
            ...newIssue,
            [name]: value
        });
    };

    const getStatusColor = (status) => {
        try {
            switch (status) {
                case 'Open':
                    return colors.redAccent[500] || '#f44336';
                case 'In Progress':
                    return colors.blueAccent[500] || '#2196f3';
                case 'Resolved':
                    return "#000000";
                default:
                    return colors.grey[500] || '#9e9e9e';
            }
        } catch (error) {
            // Fallback colors if colors object is not fully initialized
            switch (status) {
                case 'Open':
                    return '#f44336'; // red
                case 'In Progress':
                    return '#2196f3'; // blue
                case 'Resolved':
                    return '#000000'; // black
                default:
                    return '#9e9e9e'; // grey
            }
        }
    };

    const getPriorityColor = (priority) => {
        try {
            switch (priority) {
                case 'Urgent':
                    return colors.redAccent[500] || '#f44336';
                case 'High':
                    return colors.redAccent[400] || '#ef5350';
                case 'Medium':
                    return colors.orangeAccent[500] || '#ff9800';
                case 'Low':
                    return "#000000";
                default:
                    return colors.grey[500] || '#9e9e9e';
            }
        } catch (error) {
            // Fallback colors if colors object is not fully initialized
            switch (priority) {
                case 'Urgent':
                    return '#f44336'; // red
                case 'High':
                    return '#ef5350'; // light red
                case 'Medium':
                    return '#ff9800'; // orange
                case 'Low':
                    return '#000000'; // black
                default:
                    return '#9e9e9e'; // grey
            }
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: colors.primary[400],
        color: colors.grey[100],
        boxShadow: 24,
        p: 4,
        borderRadius: 2
    };

    const statusOptions = ["Open", "In Progress", "Resolved"];
    const priorityOptions = ["Urgent", "High", "Medium", "Low"];

    // Handle download reports
    const handleDownloadReports = () => {
        alert('Downloading Property Issues Reports...');
        // In a real app, this would generate and download a report
    };

    return (
        <Box m="20px">
            <Box 
                display="flex" 
                flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
                justifyContent="space-between" 
                alignItems={{ xs: 'flex-start', md: 'center' }}
                gap={2}
            >
                <Header title="PROPERTY ISSUES" subtitle="Manage Property Maintenance Issues" />
                <Box 
                    display="flex" 
                    gap="10px"
                    sx={{
                        width: { xs: '100%', md: 'auto' },
                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={handleDownloadReports}
                        sx={{
                            backgroundColor: colors.primary[400],
                            color: "#000000",
                            fontSize: "14px",
                            fontWeight: "bold",
                            padding: "10px 20px",
                            flexGrow: { xs: 1, md: 0 },
                            minWidth: { xs: '120px' }
                        }}
                    >
                        Download Reports
                    </Button>
                    {/* <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddOutlinedIcon />}
                        onClick={openAddModal}
                        sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            padding: "10px 20px",
                            flexGrow: { xs: 1, md: 0 },
                            minWidth: { xs: '120px' }
                        }}
                    >
                        Add New Issue
                    </Button> */}
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                {propertyIssuesData.map(item => (
                    <Grid item key={item.id} xs={12} sm={6} md={4}>
                        <Card 
                            sx={{ 
                                backgroundColor: colors.primary[400],
                                color: colors.grey[100],
                                boxShadow: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant="h4" fontWeight="bold" mb={1}>
                                    {item.propertyName}
                                </Typography>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <Typography variant="body1" fontWeight="bold" color={getStatusColor(item.status)}>
                                        {item.status}
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color={getPriorityColor(item.priority)}>
                                        {item.priority} Priority
                                    </Typography>
                                </Box>
                                {/* Hide detailed information */}
                            </CardContent>
                            <CardActions>
                                <IconButton 
                                    onClick={() => openEditModal(item)}
                                    sx={{ color: "#000000" }}
                                >
                                    <EditOutlinedIcon />
                                </IconButton>
                                <IconButton 
                                    onClick={() => handleDelete(item.id)}
                                    sx={{ color: colors.redAccent[400] }}
                                >
                                    <DeleteOutlinedIcon />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Edit Modal */}
            <Modal open={isModalOpen} onClose={closeModal}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" mb={2}>Edit Property Issue</Typography>
                    <form onSubmit={handleEditSubmit}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Property Name"
                            name="propertyName"
                            defaultValue={selectedIssue?.propertyName}
                            required
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Description"
                            name="description"
                            multiline
                            rows={3}
                            defaultValue={selectedIssue?.description}
                            required
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                defaultValue={selectedIssue?.status}
                                label="Status"
                                required
                            >
                                {statusOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Priority</InputLabel>
                            <Select
                                name="priority"
                                defaultValue={selectedIssue?.priority}
                                label="Priority"
                                required
                            >
                                {priorityOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Reported By"
                            name="reportedBy"
                            defaultValue={selectedIssue?.reportedBy}
                            required
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            type="date"
                            label="Report Date"
                            name="reportDate"
                            defaultValue={selectedIssue?.reportDate}
                            required
                            InputLabelProps={{ shrink: true }}
                        />
                        <Box mt={3} display="flex" justifyContent="space-between">
                            <Button variant="contained" color="secondary" type="submit">
                                Save Changes
                            </Button>
                            <Button variant="outlined" onClick={closeModal}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            {/* Add Modal */}
            <Modal open={isAddModalOpen} onClose={closeAddModal}>
                <Box sx={modalStyle}>
                    <Typography variant="h4" mb={2}>Add New Issue</Typography>
                    <form onSubmit={handleAddSubmit}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Property Name"
                            name="propertyName"
                            value={newIssue.propertyName}
                            onChange={handleInputChange}
                            required
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Description"
                            name="description"
                            multiline
                            rows={3}
                            value={newIssue.description}
                            onChange={handleInputChange}
                            required
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={newIssue.status}
                                label="Status"
                                onChange={handleInputChange}
                                required
                            >
                                {statusOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Priority</InputLabel>
                            <Select
                                name="priority"
                                value={newIssue.priority}
                                label="Priority"
                                onChange={handleInputChange}
                                required
                            >
                                {priorityOptions.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Reported By"
                            name="reportedBy"
                            value={newIssue.reportedBy}
                            onChange={handleInputChange}
                            required
                        />
                        <TextField
                            fullWidth
                            margin="normal"
                            type="date"
                            label="Report Date"
                            name="reportDate"
                            value={newIssue.reportDate}
                            onChange={handleInputChange}
                            required
                            InputLabelProps={{ shrink: true }}
                        />
                        <Box mt={3} display="flex" justifyContent="space-between">
                            <Button variant="contained" color="secondary" type="submit">
                                Add Issue
                            </Button>
                            <Button variant="outlined" onClick={closeAddModal}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>
        </Box>
    );
};

export default PropertyIssuesPage;
